import moment from "moment";

function checkNumberLuhn(input: string) {
  const strippedInput = input.replace(/-|\+/g, "");
  if (input.length === 12) {
    return luhn10(strippedInput.substring(2, 12));
  } else {
    return luhn10(strippedInput);
  }
}

export function isPNR(input: string): boolean {
  const pnrPattern =
    /^(1[8-9]|2[0-1])?([0-9]{2})(0[1-9]|1[0-2])(0[1-9]|[12]\d|3[01])(\d{4})$/;

  if (!pnrPattern.test(input)) {
    return false;
  }

  if (!checkZeroValue(input)) {
    return false;
  }

  return checkNumberLuhn(input);
}

export function isPNRandElder(input: string, ageForElder: number) {
  return isPNR(input) && isElder(input, ageForElder);
}

function isElder(input: string, ageForElder: number) {
  let limitYear = new Date().getFullYear() - ageForElder;
  const formattedInput = Number.parseInt(input.substring(0, 8));

  let elderLimit = new Date();
  elderLimit.setFullYear(limitYear);
  let formattedElderLimit = elderLimit.toLocaleDateString("sv");
  if (formattedElderLimit.includes("-")) {
    formattedElderLimit = formattedElderLimit.replaceAll("-", "");
  }
  let numberElderLimit = Number.parseInt(formattedElderLimit);

  if (input.length >= 12) {
    return numberElderLimit - formattedInput >= 0;
  } else {
    return false;
  }
}

export function isORGNR(input: string): boolean {
  const orgNrPattern = /^([0-9]+)$/;

  if (input.includes("-") || input.includes("+")) {
    var occurences = input.length - input.replace(/-|\+/g, "").length;
    if (occurences > 1) {
      return false;
    }
    var index = input.indexOf("-");
    if (index === -1) {
      index = input.indexOf("+");
    }
    if (index !== input.length - 5) {
      return false;
    }
  }

  input = input.replace(/-|\+/g, "");

  if (input.length !== 10 && input.length !== 12) {
    return false;
  }

  if (!orgNrPattern.test(input)) {
    return false;
  }

  return checkNumberLuhn(input);
}

export function isSNR(input: string): boolean {
  const snrPattern =
    /^(1[8-9]|2[0-1])?([0-9]{2})(0[1-9]|1[0-2])(6[1-9]|[78]\d|9[01])(\d{4})$/;
  const shortSnrPattern =
    /^([0-9]{2})(0[1-9]|1[0-2])(6[1-9]|[78]\d|9[01])(-|\+)?(\d{4})$/;

  if (!snrPattern.test(input) && !shortSnrPattern.test(input)) {
    return false;
  }

  return checkNumberLuhn(input);
}

export function isNRID(input: string): boolean {
  let isValidNRID = true;

  // tslint:disable-next-line
  const nridPattern =
    /^(00|2[2-9]|[3-9][0-9])([0-9]{2})(((0[1-9]|1[0-2])(0[1-9]|[12]\d|3[01]))|(([2-9][0-9])([4-5][0-9])))(-)?(((?![IOQVWioqvw])[a-zA-Z]){2})([0-9]|((?![IOQVWioqvw])[a-zA-Z]))([0-9])$/;
  if (!nridPattern.test(input)) {
    isValidNRID = false;
  }

  const strippedInput = input.replace(/-/g, "");

  if (!luhn10(strippedInput)) {
    isValidNRID = false;
  }

  return isValidNRID;
}

export function luhn10(value: string): boolean {
  let even = false;
  let nCheck = 0;

  for (let i = value.length - 1; i >= 0; i--) {
    const cDigit = value.charAt(i);
    let nDigit = parseInt(cDigit, 10);

    if (isNaN(nDigit)) {
      let asciiDecimalValue = value.charCodeAt(i);

      if (asciiDecimalValue) {
        if (even) {
          asciiDecimalValue *= 2;
        }

        const asciiDecimalStringValue = asciiDecimalValue.toString();

        for (let k = asciiDecimalStringValue.length - 1; k >= 0; k--) {
          const asciiDigit = asciiDecimalStringValue.charAt(k);
          nCheck += parseInt(asciiDigit, 10);
        }
      }
    } else {
      if (even) {
        nDigit *= 2;

        if (nDigit > 9) {
          nDigit -= 9;
        }
      }

      nCheck += nDigit;
    }
    even = !even;
  }
  return nCheck % 10 === 0;
}

export function formatIdentityInput(input: string): string {
  if (isPNR(input) || isSNR(input)) {
    return formatPNRorSNR(input);
  } else if (isNRID(input)) {
    return formatNRID(input);
  } else {
    return input;
  }
}

export function formatPNRorSNR(input: string): string {
  const currentYear = parseInt(moment().format("YYYY"), 10);
  let century = Math.trunc(currentYear / 100);

  const strippedString = input.replace(/-|\+/g, "");

  if (input.length >= 12) {
    return strippedString;
  }

  const inputYear = parseInt(input.substring(0, 2), 10);
  const isOlderThanHundredYears = input.includes("+");

  // tslint:disable-next-line
  const isBornInTwentiethCentury =
    (currentYear % 100 >= inputYear && isOlderThanHundredYears) ||
    (currentYear % 100 < inputYear && !isOlderThanHundredYears);
  const isBornNineteenthCentury =
    currentYear % 100 < inputYear && isOlderThanHundredYears;

  if (isBornInTwentiethCentury) {
    century -= 1;
  }

  if (isBornNineteenthCentury) {
    century -= 2;
  }

  return century.toString() + strippedString;
}

export function formatNRID(input: string): string {
  return input.replace(/-/g, "");
}

function checkZeroValue(input: string) {
  let string = input;
  if (input.length === 12) {
    string = input.substring(2, 12);
  }
  const subString = string.substring(6, 9);
  if (parseInt(subString) > 0) {
    return true;
  }

  return false;
}
